import * as React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import {
  Activity,
  AlertCircle,
  Check,
  Link as LinkIcon,
  MessageSquare,
  Send,
  Shield,
  X,
  type LucideIcon,
} from 'lucide-react';

import { Spinner } from '@/components/ui/spinner';
import { ProvenanceChip } from '@/components/ui/provenance-chip';
import { ApiError } from '@/lib/api/client';
import {
  RAID_REGISTER_TYPE,
  useCreateRegisterEntryMutation,
  type CreateRegisterEntryInput,
  type RegisterEntry,
} from '@/lib/api/registers';
import { useCreateRfiMutation } from '@/lib/api/rfis';
import { useCreateFindingMutation } from '@/lib/api/health-findings';
import { useCreateInterviewNoteMutation } from '@/lib/api/interviews';
import { cn } from '@/lib/utils/cn';

/**
 * T-0116 / E04-F01 Scenario 3 — Generic create-entry modal (`data-create`).
 *
 * Every line the modal shows is for the person adding the entry: no spec
 * references or notes about how the app is wired (C2-32).
 *
 * Visual contract: `design_handoff_caliber_v2/prototype/modals-create.js`
 * → `window.Modals.openCreate`. One modal that adapts to each register's
 * schema: risk / issue / RFI / finding / interview. Deliverables have their
 * own drawer on Contract → Deliverables.
 *
 * Behavior:
 *   - Schema-driven from the `kind` prop. Each kind maps to a register
 *     type + a closed-schema payload shape + a curated set of UI fields.
 *     Risks and issues both post to the RAID register
 *     (`RAID_REGISTER_TYPE`, the type the list and filter bar read) and
 *     carry their entry kind in `payload.framework_type` (T-0226 / C2-08).
 *   - Required fields are gated client-side; the Save button stays
 *     disabled until every required field is filled (matches the
 *     prototype's `canSave()`).
 *   - On Save → POST `/api/v1/engagements/:engagement_id/registers/:type`
 *     with the typed payload. The backend re-validates against the
 *     framework_definition's JSONB (T-0030) — surfacing validation errors
 *     means the form is out of sync with the framework, which we surface
 *     verbatim from the API response.
 *   - Origin is always `manually_authored` (the prototype's
 *     `origin: 'manual'`). Provenance chip on the footer shows `manual`.
 *   - `framework_definition_id` is the load-bearing field the backend
 *     keys against; the v1 modal requires the caller to supply one per
 *     register-type from props. The host page resolves it from
 *     `useReadinessRubricQuery` / `useFrameworkDefaultsQuery` etc.
 *
 * Backend gaps (flagged):
 *   - The "evidence link" field in the prototype is a separate write —
 *     `POST .../evidence-links` — that needs to fire after the register
 *     create lands. v1 collects an optional evidence note but defers
 *     the link write to a follow-up the host page can wire (the host
 *     gets the created entry id back via `onCreated`).
 *   - The `interview` kind needs a separate route (`/health/interviews`)
 *     not under the register endpoint; v1 disables that kind unless the
 *     host opts in via `disabledKinds`.
 */

export type CreateKind = 'risk' | 'issue' | 'rfi' | 'finding' | 'interview';

type FieldKind = 'text' | 'date' | 'select' | 'textarea' | 'evidence';

interface FieldSpec {
  id: string;
  label: string;
  kind: FieldKind;
  required?: boolean;
  options?: ReadonlyArray<string>;
  placeholder?: string;
  help?: string;
}

interface KindSpec {
  kind: CreateKind;
  /** Display label ("Risk", "RFI", …). */
  label: string;
  /** Modal header line: "Create new risk". */
  title: string;
  /** Modal subtitle line under the title. */
  sub: string;
  /** Header glyph. */
  icon: LucideIcon;
  /** The backend `register_type` value used in the POST path. */
  registerType: string;
  /** Ordered field list. */
  fields: ReadonlyArray<FieldSpec>;
  /**
   * Build the payload JSON the backend stores. Receives raw form values
   * keyed by field id; returns a flat record the framework_definition
   * validates.
   */
  buildPayload: (values: Record<string, string>) => Record<string, unknown>;
  /**
   * Which backend surface Save posts to. 'register' (default) is the
   * generic register-entry POST; the other three route to their dedicated
   * feature endpoints (RFIs, health findings, interview notes) so the
   * rows land in the tables the feature screens actually read.
   */
  submitVia?: 'register' | 'rfi' | 'finding' | 'interview';
}

const KIND_SPECS: Record<CreateKind, KindSpec> = {
  risk: {
    kind: 'risk',
    label: 'Risk',
    title: 'Create new risk',
    sub: 'Added to the RAID register as your entry and recorded in the audit trail.',
    icon: Shield,
    registerType: RAID_REGISTER_TYPE,
    fields: [
      { id: 'title', label: 'Title', kind: 'text', required: true },
      {
        id: 'domain',
        label: 'Domain',
        kind: 'select',
        required: true,
        options: [
          'Scope',
          'Schedule',
          'Budget',
          'Quality',
          'Risk Management',
          'Vendor Performance',
          'Governance',
          'Data',
          'Change Management',
        ],
      },
      {
        id: 'severity',
        label: 'Severity',
        kind: 'select',
        required: true,
        options: ['high', 'medium', 'low'],
      },
      {
        id: 'likelihood',
        label: 'Likelihood',
        kind: 'select',
        required: true,
        options: ['certain', 'likely', 'possible', 'unlikely'],
      },
      { id: 'owner', label: 'Owner', kind: 'text', required: true, placeholder: 'e.g. City PMO' },
      {
        id: 'status',
        label: 'Status',
        kind: 'select',
        required: true,
        options: ['Open', 'Mitigating', 'Closed', 'Accepted'],
      },
      {
        id: 'description',
        label: 'Description',
        kind: 'textarea',
        required: true,
        help: 'Describe the risk in 1–3 sentences. Cite recent evidence where possible.',
      },
      { id: 'evidence', label: 'Evidence link', kind: 'evidence' },
    ],
    buildPayload: (v) => ({
      framework_type: 'risk',
      title: v.title,
      domain: v.domain,
      severity: v.severity,
      likelihood: v.likelihood,
      owner: v.owner,
      status: v.status,
      description: v.description,
      ...(v.evidence ? { evidence_note: v.evidence } : {}),
    }),
  },
  issue: {
    kind: 'issue',
    label: 'Issue',
    title: 'Create new issue',
    sub: 'Added to the RAID register as your entry and recorded in the audit trail.',
    icon: AlertCircle,
    registerType: RAID_REGISTER_TYPE,
    fields: [
      { id: 'title', label: 'Title', kind: 'text', required: true },
      {
        id: 'severity',
        label: 'Severity',
        kind: 'select',
        required: true,
        options: ['high', 'medium', 'low'],
      },
      { id: 'owner', label: 'Owner', kind: 'text', required: true, placeholder: 'e.g. City PMO' },
      {
        id: 'status',
        label: 'Status',
        kind: 'select',
        required: true,
        options: ['Open', 'In progress', 'Resolved'],
      },
      {
        id: 'resolution',
        label: 'Resolution',
        kind: 'textarea',
        help: 'How the issue is being addressed.',
      },
      { id: 'evidence', label: 'Evidence link', kind: 'evidence' },
    ],
    buildPayload: (v) => ({
      framework_type: 'issue',
      title: v.title,
      severity: v.severity,
      owner: v.owner,
      status: v.status,
      ...(v.resolution ? { resolution: v.resolution } : {}),
      ...(v.evidence ? { evidence_note: v.evidence } : {}),
    }),
  },
  rfi: {
    kind: 'rfi',
    label: 'RFI',
    title: 'Create new RFI',
    sub: 'Moves from Open to Sent, Responded and Closed. Recorded in the audit trail.',
    icon: Send,
    registerType: 'rfi_register',
    submitVia: 'rfi',
    fields: [
      {
        id: 'title',
        label: 'Question',
        kind: 'text',
        required: true,
        placeholder: 'e.g. Confirm vendor SLA during hypercare',
      },
      {
        id: 'recipient_kind',
        label: 'Recipient',
        kind: 'select',
        required: true,
        options: ['vendor', 'pmo'],
      },
      {
        id: 'priority',
        label: 'Priority',
        kind: 'select',
        required: true,
        options: ['critical', 'high', 'medium', 'low'],
      },
      { id: 'due', label: 'Due date', kind: 'date', required: true },
      {
        id: 'body',
        label: 'Detail',
        kind: 'textarea',
        required: true,
        help: 'Explain the question + context. Cite supporting clauses if applicable.',
      },
      { id: 'evidence', label: 'Evidence link', kind: 'evidence' },
    ],
    buildPayload: (v) => ({
      title: v.title,
      recipient_kind: v.recipient_kind,
      priority: v.priority,
      due: v.due,
      body: v.body,
      ...(v.evidence ? { evidence_note: v.evidence } : {}),
    }),
  },
  finding: {
    kind: 'finding',
    label: 'Finding',
    title: 'Create new finding',
    sub: 'Added to the consolidated findings register and recorded in the audit trail.',
    icon: Activity,
    registerType: 'finding',
    submitVia: 'finding',
    fields: [
      { id: 'title', label: 'Title', kind: 'text', required: true },
      {
        id: 'category',
        label: 'Category',
        kind: 'select',
        required: true,
        options: [
          'Governance',
          'Cutover',
          'Quality',
          'Data',
          'Org. Readiness',
          'Vendor Perf.',
          'Schedule',
          'Budget',
        ],
      },
      {
        id: 'severity',
        label: 'Severity',
        kind: 'select',
        required: true,
        options: ['critical', 'high', 'medium', 'low'],
      },
      {
        id: 'source_kind',
        label: 'Source',
        kind: 'select',
        required: true,
        options: ['manual', 'interview', 'document', 'status_report', 'other'],
      },
      { id: 'description', label: 'Description', kind: 'textarea', required: true },
      { id: 'evidence', label: 'Evidence link', kind: 'evidence' },
    ],
    buildPayload: (v) => ({
      title: v.title,
      category: v.category,
      severity: v.severity,
      source_kind: v.source_kind,
      description: v.description,
      ...(v.evidence ? { evidence_note: v.evidence } : {}),
    }),
  },
  interview: {
    kind: 'interview',
    label: 'Interview',
    title: 'Schedule new interview',
    sub: 'A stakeholder interview for the health check.',
    icon: MessageSquare,
    registerType: 'interview_note',
    submitVia: 'interview',
    fields: [
      {
        id: 'who',
        label: 'Stakeholder',
        kind: 'text',
        required: true,
        placeholder: 'e.g. A. Reyes',
      },
      {
        id: 'role',
        label: 'Role',
        kind: 'text',
        required: true,
        placeholder: 'e.g. City PMO Director',
      },
      { id: 'date', label: 'Date', kind: 'date', required: true },
      {
        id: 'notes',
        label: 'Initial notes',
        kind: 'textarea',
        help: 'Optional — full transcript can be added after the session.',
      },
    ],
    buildPayload: (v) => ({
      who: v.who,
      role: v.role,
      date: v.date,
      ...(v.notes ? { notes: v.notes } : {}),
    }),
  },
};

export interface CreateFormModalProps {
  engagementId: string;
  kind: CreateKind | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * The framework_definition_id the backend will validate the payload
   * against. The host page resolves this from its own framework-defaults
   * cache and hands it in per-kind. When omitted, the modal surfaces a
   * banner explaining the host hasn't wired the framework yet.
   */
  frameworkDefinitionId?: string;
  /** Optional kinds the host wants to grey-out. Useful for interview routing. */
  disabledKinds?: ReadonlyArray<CreateKind>;
  /**
   * Called after a successful POST. The host can use the returned entry
   * id to wire follow-up writes (evidence-link, scheduling). The modal
   * closes itself before this fires.
   */
  onCreated?: (entry: RegisterEntry) => void;
}

export function CreateFormModal({
  engagementId,
  kind,
  open,
  onOpenChange,
  frameworkDefinitionId,
  disabledKinds,
  onCreated,
}: CreateFormModalProps) {
  const spec = kind ? KIND_SPECS[kind] : null;
  const disabled = kind ? (disabledKinds?.includes(kind) ?? false) : false;
  const titleId = kind ? `create-form-title-${kind}` : 'create-form-title';

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 z-40 bg-scrim',
            'opacity-0 motion-safe:transition-opacity motion-safe:duration-150',
            'data-[state=open]:opacity-100',
          )}
        />
        <Dialog.Content
          aria-labelledby={titleId}
          aria-describedby={undefined}
          className={cn(
            'fixed left-1/2 top-1/2 z-50 grid w-[min(720px,94vw)] max-h-[92vh]',
            '-translate-x-1/2 -translate-y-1/2 grid-rows-[auto,1fr,auto]',
            'overflow-hidden rounded-modal bg-paper-0 shadow-modal',
            'opacity-0 motion-safe:transition-opacity motion-safe:duration-150',
            'data-[state=open]:opacity-100',
          )}
        >
          {spec ? (
            <CreateBody
              key={spec.kind}
              spec={spec}
              titleId={titleId}
              engagementId={engagementId}
              frameworkDefinitionId={frameworkDefinitionId}
              disabled={disabled}
              onClose={() => onOpenChange(false)}
              onCreated={onCreated}
            />
          ) : (
            <div className="grid place-items-center p-10">
              <p className="text-meta text-ink-500">No entry kind selected.</p>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function CreateBody({
  spec,
  titleId,
  engagementId,
  frameworkDefinitionId,
  disabled,
  onClose,
  onCreated,
}: {
  spec: KindSpec;
  titleId: string;
  engagementId: string;
  frameworkDefinitionId: string | undefined;
  disabled: boolean;
  onClose: () => void;
  onCreated: ((entry: RegisterEntry) => void) | undefined;
}) {
  const [values, setValues] = React.useState<Record<string, string>>(() => initialValues(spec));
  const [showValidation, setShowValidation] = React.useState(false);
  const [serverError, setServerError] = React.useState<string | null>(null);

  // Reset the form whenever the kind switches.
  React.useEffect(() => {
    setValues(initialValues(spec));
    setShowValidation(false);
    setServerError(null);
  }, [spec]);

  const mutation = useCreateRegisterEntryMutation(engagementId, spec.registerType);
  const rfiMutation = useCreateRfiMutation(engagementId);
  const findingMutation = useCreateFindingMutation(engagementId);
  const interviewMutation = useCreateInterviewNoteMutation(engagementId);
  const submitVia = spec.submitVia ?? 'register';
  const isPending =
    submitVia === 'rfi'
      ? rfiMutation.isPending
      : submitVia === 'finding'
        ? findingMutation.isPending
        : submitVia === 'interview'
          ? interviewMutation.isPending
          : mutation.isPending;
  const Icon = spec.icon;

  function isFilled(f: FieldSpec): boolean {
    const v = (values[f.id] ?? '').trim();
    return v.length > 0;
  }
  function canSave(): boolean {
    return spec.fields.every((f) => !f.required || isFilled(f));
  }

  function handleSubmit(ev: React.FormEvent) {
    ev.preventDefault();
    if (!canSave()) {
      setShowValidation(true);
      return;
    }
    if (disabled) return;
    setServerError(null);

    const onError = (e: unknown) => {
      if (e instanceof ApiError) {
        setServerError(`${e.code}: ${e.message}`);
      } else {
        setServerError('Create failed — see network log for details.');
      }
    };

    // RFIs, findings, and interviews post to their dedicated feature
    // endpoints so the rows land where those screens actually read.
    if (submitVia === 'rfi') {
      rfiMutation.mutate(
        {
          body: {
            title: values.title ?? '',
            detail: values.body ?? '',
            recipient_kind: (values.recipient_kind ?? 'vendor') as 'vendor' | 'pmo',
            recipient_info: {
              ...(values.priority ? { priority: values.priority } : {}),
              ...(values.due ? { due: values.due } : {}),
              ...(values.evidence ? { evidence_note: values.evidence } : {}),
            },
          },
          idempotencyKey: cryptoRandom(),
        },
        { onSuccess: () => onClose(), onError },
      );
      return;
    }
    if (submitVia === 'finding') {
      findingMutation.mutate(
        {
          title: values.title ?? '',
          severity: (values.severity ?? 'medium') as 'critical' | 'high' | 'medium' | 'low',
          category: values.category ?? 'Governance',
          source_kind: (values.source_kind ?? 'manual') as
            | 'status_report'
            | 'interview'
            | 'document'
            | 'manual'
            | 'other',
          description: values.evidence
            ? `${values.description ?? ''}\n\nEvidence: ${values.evidence}`
            : (values.description ?? null),
        },
        { onSuccess: () => onClose(), onError },
      );
      return;
    }
    if (submitVia === 'interview') {
      const who = (values.who ?? '').trim();
      const role = (values.role ?? '').trim();
      interviewMutation.mutate(
        {
          title: role ? `Interview — ${who} (${role})` : `Interview — ${who}`,
          body_markdown: values.notes?.trim() ? values.notes : null,
          interviewee_label: who || null,
          interview_date: values.date || null,
        },
        { onSuccess: () => onClose(), onError },
      );
      return;
    }

    const payload = spec.buildPayload(values);
    const input: CreateRegisterEntryInput = {
      // Optional — when the host has no framework cache the API resolves
      // the per-register-type manual default itself (Tier 1.2).
      ...(frameworkDefinitionId ? { framework_definition_id: frameworkDefinitionId } : {}),
      payload,
      origin: 'manually_authored',
    };
    mutation.mutate(
      { input, idempotencyKey: cryptoRandom() },
      {
        onSuccess: (entry) => {
          onCreated?.(entry);
          onClose();
        },
        onError,
      },
    );
  }

  // Pair adjacent "short" fields (select / date / owner-style text) into
  // two-column rows for visual density. Textareas + evidence always span.
  const rows = pairFields(spec.fields);

  return (
    <>
      <div className="flex items-start justify-between gap-3 border-b border-ink-100 bg-paper-50 px-6 py-4">
        <div className="min-w-0">
          <p className="flex items-center gap-1.5 text-meta uppercase tracking-wide text-ink-500">
            <Icon className="h-3 w-3" aria-hidden="true" />
            {spec.label} &middot; new entry
          </p>
          <Dialog.Title id={titleId} className="mt-1 text-h2 font-semibold text-ink-900">
            {spec.title}
          </Dialog.Title>
          <p className="mt-1 text-meta text-ink-500">{spec.sub}</p>
        </div>
        <Dialog.Close
          aria-label="Close create form"
          onClick={onClose}
          className={cn(
            'inline-flex h-8 w-8 items-center justify-center rounded-card',
            'text-ink-500 hover:bg-paper-100 hover:text-ink-900',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-300',
          )}
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </Dialog.Close>
      </div>

      <form
        id="create-entry-form"
        className="overflow-y-auto px-6 py-5"
        onSubmit={handleSubmit}
        noValidate
      >
        {disabled ? (
          <div className="mb-4 rounded-card border border-accent-amber-100 bg-accent-amber-50 p-3 text-meta text-accent-amber-700">
            This kind of entry can&apos;t be created from this page. Open its own screen to add one.
          </div>
        ) : null}

        <div className="space-y-4">
          {rows.map((row, i) => (
            <div
              key={i}
              className={cn('grid gap-3', row.length === 2 ? 'grid-cols-2' : 'grid-cols-1')}
            >
              {row.map((f) => (
                <Field
                  key={f.id}
                  spec={f}
                  value={values[f.id] ?? ''}
                  onChange={(v) => setValues((prev) => ({ ...prev, [f.id]: v }))}
                  missing={showValidation && (f.required ?? false) && !isFilled(f)}
                  disabled={disabled || isPending}
                />
              ))}
            </div>
          ))}

          <div className="flex items-start gap-3 rounded-card border border-paper-200 bg-paper-50 p-3">
            <ProvenanceChip origin="manual" />
            <p className="text-meta leading-[18px] text-ink-700">
              This entry is <strong>manually authored</strong> (no AI extraction). It will be saved
              with origin <span className="font-mono">manually_authored</span> and the consultant as
              actor. Audit-logged.
            </p>
          </div>

          {serverError ? (
            <div className="rounded-card border border-cite-broken bg-paper-0 p-3 text-meta text-cite-broken">
              {serverError}
            </div>
          ) : null}
        </div>
      </form>

      <div className="flex items-center justify-between gap-3 border-t border-ink-100 bg-paper-50 px-6 py-3">
        <p className="text-meta text-ink-500">
          {canSave() ? 'Ready to save.' : 'Fill required fields to enable Save.'}
        </p>
        <div className="flex items-center gap-2">
          <Dialog.Close
            className={cn(
              'inline-flex h-9 items-center rounded-card border border-ink-200 bg-paper-0 px-3 text-body-sm font-medium text-ink-700',
              'hover:bg-paper-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-300',
            )}
          >
            Cancel
          </Dialog.Close>
          <button
            type="submit"
            form="create-entry-form"
            disabled={!canSave() || disabled || isPending}
            className={cn(
              'inline-flex h-9 items-center gap-1.5 rounded-card bg-ink-900 px-3 text-body-sm font-medium text-paper-0',
              'hover:bg-ink-700 disabled:cursor-not-allowed disabled:opacity-50',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-300',
            )}
          >
            {isPending ? <Spinner /> : <Check className="h-3.5 w-3.5" aria-hidden="true" />}
            Create {spec.label.toLowerCase()}
          </button>
        </div>
      </div>
    </>
  );
}

function Field({
  spec,
  value,
  onChange,
  missing,
  disabled,
}: {
  spec: FieldSpec;
  value: string;
  onChange: (v: string) => void;
  missing: boolean;
  disabled: boolean;
}) {
  const label = (
    <label htmlFor={`create-${spec.id}`} className="flex items-center gap-1 text-meta text-ink-700">
      <span className="font-semibold">{spec.label}</span>
      <span className="text-ink-500">{spec.required ? '· required' : '· optional'}</span>
    </label>
  );
  const errClass = missing ? 'border-cite-broken' : 'border-ink-200';
  const baseInput = cn(
    'mt-1 block w-full rounded-card border bg-paper-0 px-3 py-2 text-body-sm text-ink-900',
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-300 disabled:opacity-50',
    errClass,
  );
  if (spec.kind === 'text') {
    return (
      <div>
        {label}
        <input
          id={`create-${spec.id}`}
          type="text"
          className={baseInput}
          placeholder={spec.placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        />
        {renderHelp(spec, missing)}
      </div>
    );
  }
  if (spec.kind === 'date') {
    return (
      <div>
        {label}
        <input
          id={`create-${spec.id}`}
          type="date"
          className={baseInput}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        />
        {renderHelp(spec, missing)}
      </div>
    );
  }
  if (spec.kind === 'select') {
    return (
      <div>
        {label}
        <select
          id={`create-${spec.id}`}
          className={baseInput}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        >
          <option value="">— select —</option>
          {(spec.options ?? []).map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
        {renderHelp(spec, missing)}
      </div>
    );
  }
  if (spec.kind === 'textarea') {
    return (
      <div>
        {label}
        <textarea
          id={`create-${spec.id}`}
          className={cn(baseInput, 'min-h-[88px]')}
          placeholder={spec.placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        />
        {renderHelp(spec, missing)}
      </div>
    );
  }
  // evidence — a free-text note saved with the entry (`evidence_note`); a
  // typed evidence link to a document is a later step, so the hint says
  // what to write, not how the app will store it (C2-32).
  return (
    <div>
      {label}
      <div
        className={cn(
          'mt-1 flex items-start gap-2 rounded-card border border-dashed bg-paper-50 p-3',
          errClass,
        )}
      >
        <LinkIcon className="mt-0.5 h-3.5 w-3.5 flex-none text-ink-500" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <input
            id={`create-${spec.id}`}
            type="text"
            placeholder="e.g. SOW v2 §4.3 — page 47"
            className={cn(
              'block w-full rounded-card border-0 bg-paper-0 px-2 py-1.5 text-body-sm text-ink-900',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-300 disabled:opacity-50',
            )}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
          />
          <p className="mt-1 text-[11px] text-ink-500">
            Where the evidence is: the document, section and page.
          </p>
        </div>
      </div>
      {renderHelp(spec, missing)}
    </div>
  );
}

function renderHelp(spec: FieldSpec, missing: boolean) {
  if (missing) {
    return <p className="mt-1 text-[11px] text-cite-broken">{spec.label} is required.</p>;
  }
  if (spec.help) {
    return <p className="mt-1 text-[11px] text-ink-500">{spec.help}</p>;
  }
  return null;
}

function initialValues(spec: KindSpec): Record<string, string> {
  const out: Record<string, string> = {};
  for (const f of spec.fields) {
    out[f.id] = '';
  }
  return out;
}

function pairFields(fields: ReadonlyArray<FieldSpec>): Array<Array<FieldSpec>> {
  const rows: Array<Array<FieldSpec>> = [];
  const isShort = (f: FieldSpec | undefined) =>
    f && (f.kind === 'select' || f.kind === 'date' || (f.kind === 'text' && f.id !== 'title'));
  let i = 0;
  while (i < fields.length) {
    const f = fields[i]!;
    const next = fields[i + 1];
    if (
      isShort(f) &&
      isShort(next) &&
      f.kind !== 'evidence' &&
      (next?.kind ?? 'text') !== 'evidence'
    ) {
      rows.push([f, next!]);
      i += 2;
    } else {
      rows.push([f]);
      i += 1;
    }
  }
  return rows;
}

function cryptoRandom(): string {
  // Idempotency key — UUID when available, else a random string.
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }
  return `idem-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}
